import React, { useState, useEffect } from "react";
import axios from "axios";
import { Icon } from "./Icon";
import ImpersonateUser from "./ImpersonateUser";

interface UserAdminProps {
  authToken: string;
}

interface UserStats {
  totalUsers: number;
  totalPremiumUsers: number;
}

interface RecentUser {
  id: string;
  username: string;
  createdAt: string;
  approved: boolean;
  userType: "user" | "premium" | "vip" | "admin";
}

interface PremiumUser {
  username: string;
  lastPaymentDate: string | null;
  subscriptionStatus: string | null;
}

interface RecentLogin {
  id: string;
  username: string;
  displayName: string;
  ipAddress: string;
  deviceType: string | null;
  userAgent: string;
  createdAt: string;
}

const UserAdmin: React.FC<UserAdminProps> = ({ authToken }) => {
  const [userStats, setUserStats] = useState<UserStats>({
    totalUsers: 0,
    totalPremiumUsers: 0,
  });
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [premiumUsers, setPremiumUsers] = useState<PremiumUser[]>([]);
  const [recentLogins, setRecentLogins] = useState<RecentLogin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [statsResponse, recentResponse, premiumResponse, loginsResponse] =
        await Promise.all([
          axios.get("/api/admin/user-stats", {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
          axios.get("/api/admin/recent-users", {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
          axios.get("/api/admin/premium-users", {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
          axios.get("/api/admin/recent-logins", {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
        ]);

      setUserStats(statsResponse.data);
      setRecentUsers(recentResponse.data);
      setPremiumUsers(premiumResponse.data);
      setRecentLogins(loginsResponse.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalToggle = async (
    userId: string,
    currentStatus: boolean,
  ) => {
    try {
      await axios.patch(
        `/api/admin/users/${userId}/approval`,
        { approved: !currentStatus },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      // Update local state
      setRecentUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, approved: !currentStatus } : user,
        ),
      );
    } catch (error) {
      console.error("Error updating user approval status:", error);
      alert("Failed to update user approval status");
    }
  };

  const handleVipToggle = async (
    userId: string,
    currentType: "user" | "premium" | "vip" | "admin",
  ) => {
    // Toggle between 'user' and 'vip' (don't change premium or admin users)
    if (currentType === "premium" || currentType === "admin") {
      alert("Cannot change VIP status for premium or admin users");
      return;
    }

    const newType = currentType === "vip" ? "user" : "vip";

    try {
      await axios.patch(
        `/api/admin/users/${userId}/type`,
        { userType: newType },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      // Update local state
      setRecentUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, userType: newType } : user,
        ),
      );
    } catch (error) {
      console.error("Error updating user VIP status:", error);
      alert("Failed to update user VIP status");
    }
  };

  const StatBox = ({
    title,
    value,
    color,
  }: {
    title: string;
    value: number;
    color: string;
  }) => (
    <div
      style={{
        backgroundColor: "#2d3748",
        borderRadius: "12px",
        padding: "24px",
        border: "1px solid #4a5568",
        flex: "1",
        minWidth: "250px",
      }}
    >
      <h3
        style={{
          color: "#a0aec0",
          fontSize: "14px",
          fontWeight: "500",
          marginBottom: "8px",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {title}
      </h3>
      <p
        style={{
          color,
          fontSize: "36px",
          fontWeight: "700",
          margin: 0,
          lineHeight: 1,
        }}
      >
        {loading ? "..." : value.toLocaleString()}
      </p>
    </div>
  );

  if (error) {
    return (
      <div
        style={{
          backgroundColor: "#553c7b",
          padding: "16px",
          borderRadius: "8px",
          marginBottom: "20px",
        }}
      >
        <p style={{ color: "#fbb6ce", margin: 0 }}>
          <Icon
            name="alert-triangle"
            size={16}
            style={{ verticalAlign: "-3px", marginRight: "6px" }}
          />
          {error}
        </p>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div>
      <h3
        style={{
          fontSize: "20px",
          fontWeight: "600",
          color: "#e2e8f0",
          marginBottom: "24px",
        }}
      >
        User Statistics
      </h3>

      <div
        style={{
          display: "flex",
          gap: "20px",
          flexWrap: "wrap",
          marginBottom: "32px",
        }}
      >
        <StatBox
          title="Total Users"
          value={userStats.totalUsers}
          color="#9146FF"
        />
        <StatBox
          title="Premium Users"
          value={userStats.totalPremiumUsers}
          color="#F59E0B"
        />
      </div>

      <ImpersonateUser authToken={authToken} />

      {/* Recent Logins Section */}
      <div
        style={{
          backgroundColor: "#2d3748",
          borderRadius: "12px",
          padding: "24px",
          border: "1px solid #4a5568",
          marginTop: "40px",
          marginBottom: "24px",
        }}
      >
        <h4
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#e2e8f0",
            marginBottom: "16px",
          }}
        >
          <Icon
            name="lock"
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Recent Logins (Last 10)
        </h4>
        {loading ? (
          <p style={{ color: "#a0aec0" }}>Loading...</p>
        ) : recentLogins.length === 0 ? (
          <p style={{ color: "#a0aec0" }}>No recent logins found</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #4a5568" }}>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "12px",
                      color: "#a0aec0",
                      fontSize: "14px",
                    }}
                  >
                    User
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "12px",
                      color: "#a0aec0",
                      fontSize: "14px",
                    }}
                  >
                    IP Address
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "12px",
                      color: "#a0aec0",
                      fontSize: "14px",
                    }}
                  >
                    Device
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "12px",
                      color: "#a0aec0",
                      fontSize: "14px",
                    }}
                  >
                    Time
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentLogins.map((login) => (
                  <tr
                    key={login.id}
                    style={{ borderBottom: "1px solid #2d3748" }}
                  >
                    <td style={{ padding: "12px" }}>
                      <div>
                        <div style={{ color: "#9146FF", fontWeight: "500" }}>
                          {login.username}
                        </div>
                        <div style={{ color: "#718096", fontSize: "12px" }}>
                          {login.displayName}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "12px", color: "#cbd5e0" }}>
                      {login.ipAddress}
                    </td>
                    <td style={{ padding: "12px", color: "#cbd5e0" }}>
                      {login.deviceType ? (
                        <span
                          style={{
                            padding: "4px 8px",
                            borderRadius: "4px",
                            backgroundColor:
                              login.deviceType === "mobile"
                                ? "#4C1D95"
                                : login.deviceType === "tablet"
                                  ? "#1E3A8A"
                                  : "#065F46",
                            fontSize: "12px",
                            textTransform: "capitalize",
                          }}
                        >
                          {login.deviceType}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        color: "#a0aec0",
                        fontSize: "14px",
                      }}
                    >
                      {formatDate(login.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Users and Premium Users Lists */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: "24px",
        }}
      >
        {/* Recent Users List */}
        <div
          style={{
            backgroundColor: "#2d3748",
            borderRadius: "12px",
            padding: "24px",
            border: "1px solid #4a5568",
            gridColumn: "1 / -1",
          }}
        >
          <h4
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#e2e8f0",
              marginBottom: "16px",
            }}
          >
            <Icon
              name="calendar"
              size={18}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Recent Users (Last 10)
          </h4>
          {loading ? (
            <p style={{ color: "#a0aec0" }}>Loading...</p>
          ) : recentUsers.length === 0 ? (
            <p style={{ color: "#a0aec0" }}>No users found</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "12px",
                        borderBottom: "2px solid #4a5568",
                        color: "#a0aec0",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Username
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "12px",
                        borderBottom: "2px solid #4a5568",
                        color: "#a0aec0",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Created
                    </th>
                    <th
                      style={{
                        textAlign: "center",
                        padding: "12px",
                        borderBottom: "2px solid #4a5568",
                        color: "#a0aec0",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Approved
                    </th>
                    <th
                      style={{
                        textAlign: "center",
                        padding: "12px",
                        borderBottom: "2px solid #4a5568",
                        color: "#a0aec0",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      VIP
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recentUsers.map((user, index) => (
                    <tr
                      key={user.id}
                      style={{
                        borderBottom: "1px solid #4a5568",
                      }}
                    >
                      <td
                        style={{
                          padding: "12px",
                          color: "#9146FF",
                          fontWeight: "500",
                        }}
                      >
                        {user.username}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          color: "#a0aec0",
                          fontSize: "14px",
                        }}
                      >
                        {formatDate(user.createdAt)}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                        }}
                      >
                        <button
                          onClick={() =>
                            handleApprovalToggle(user.id, user.approved)
                          }
                          style={{
                            width: "60px",
                            height: "28px",
                            borderRadius: "14px",
                            border: "none",
                            backgroundColor: user.approved
                              ? "#48bb78"
                              : "#718096",
                            cursor: "pointer",
                            position: "relative",
                            transition: "background-color 0.2s",
                            padding: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = "0.8";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = "1";
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: "2px",
                              left: user.approved ? "34px" : "2px",
                              width: "24px",
                              height: "24px",
                              borderRadius: "50%",
                              backgroundColor: "white",
                              transition: "left 0.2s",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            }}
                          />
                          <span
                            style={{
                              position: "absolute",
                              left: user.approved ? "8px" : "auto",
                              right: user.approved ? "auto" : "8px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: "10px",
                              fontWeight: "bold",
                              color: "white",
                            }}
                          >
                            {user.approved ? "ON" : "OFF"}
                          </span>
                        </button>
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                        }}
                      >
                        {user.userType === "premium" ||
                        user.userType === "admin" ? (
                          <span
                            style={{
                              padding: "4px 8px",
                              borderRadius: "4px",
                              backgroundColor:
                                user.userType === "admin"
                                  ? "#9146FF"
                                  : "#F59E0B",
                              fontSize: "12px",
                              color: "white",
                              fontWeight: "500",
                            }}
                          >
                            {user.userType === "admin" ? "ADMIN" : "PREMIUM"}
                          </span>
                        ) : (
                          <button
                            onClick={() =>
                              handleVipToggle(user.id, user.userType)
                            }
                            style={{
                              width: "60px",
                              height: "28px",
                              borderRadius: "14px",
                              border: "none",
                              backgroundColor:
                                user.userType === "vip" ? "#10B981" : "#718096",
                              cursor: "pointer",
                              position: "relative",
                              transition: "background-color 0.2s",
                              padding: 0,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = "0.8";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = "1";
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: "2px",
                                left: user.userType === "vip" ? "34px" : "2px",
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                backgroundColor: "white",
                                transition: "left 0.2s",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                              }}
                            />
                            <span
                              style={{
                                position: "absolute",
                                left: user.userType === "vip" ? "6px" : "auto",
                                right: user.userType === "vip" ? "auto" : "6px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: "10px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              {user.userType === "vip" ? "VIP" : "OFF"}
                            </span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Premium Users List */}
        <div
          style={{
            backgroundColor: "#2d3748",
            borderRadius: "12px",
            padding: "24px",
            border: "1px solid #4a5568",
          }}
        >
          <h4
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#e2e8f0",
              marginBottom: "16px",
            }}
          >
            <Icon
              name="gem"
              size={18}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Premium Users (By Last Payment)
          </h4>
          {loading ? (
            <p style={{ color: "#a0aec0" }}>Loading...</p>
          ) : premiumUsers.length === 0 ? (
            <p style={{ color: "#a0aec0" }}>No premium users found</p>
          ) : (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {premiumUsers.map((user, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px",
                    backgroundColor: "#1a202c",
                    borderRadius: "8px",
                    border: "1px solid #4a5568",
                  }}
                >
                  <span style={{ color: "#F59E0B", fontWeight: "500" }}>
                    {user.username}
                  </span>
                  <span style={{ color: "#a0aec0", fontSize: "14px" }}>
                    {user.lastPaymentDate
                      ? formatDate(user.lastPaymentDate)
                      : "No payment recorded"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(UserAdmin);
